import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAssets } from "../../assets/api/assets";
import Button from "../../common/components/Button";
import { SPACING } from "../../common/constants/spacing";
import { useProject } from "../../common/contexts/ProjectContext";
import { useExpense, useExpenses } from "../api/expenses";

const AddExpenseModal = ({
  open,
  onClose,
  onExpenseCreated,
  editingExpense,
}) => {
  const { t } = useTranslation("expenses");
  const [formData, setFormData] = useState({
    name: "",
    value: "",
    type: "static",
    user: "",
    asset: "",
  });

  const { createExpense, creating } = useExpenses();
  const { project } = useProject();
  const { assets, loading: assetsLoading, error: assetsError } = useAssets();
  const { updateExpense, updating } = useExpense(editingExpense?.id);

  const [error, setError] = useState(null);
  const loading = creating || updating;

  // Map project users to members format for the dropdown
  const members =
    project?.users?.map((pu) => ({
      id: pu.user_details?.id,
      name: pu.user_details?.name,
      email: pu.user_details?.email,
      role: pu.role,
    })) || [];

  const dataLoading = assetsLoading;
  const dataError = assetsError;

  useEffect(() => {
    if (open) {
      if (editingExpense) {
        setFormData({
          name: editingExpense.name,
          value: editingExpense.value,
          type: editingExpense.type,
          user: editingExpense.user_id,
          asset: editingExpense.asset_id,
        });
      } else {
        setFormData({
          name: "",
          value: "",
          type: "static",
          user: "",
          asset: "",
        });
      }
      setError(null);
    }
  }, [open, editingExpense]);

  useEffect(() => {
    if (dataError) {
      setError(dataError);
    }
  }, [dataError]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      setError(t("validation.nameRequired"));
      return false;
    }
    if (!formData.value || parseFloat(formData.value) <= 0) {
      setError(t("validation.valueGreaterThanZero"));
      return false;
    }
    if (formData.type === "percentage" && parseFloat(formData.value) > 100) {
      setError(t("validation.percentageMax"));
      return false;
    }
    if (!formData.user) {
      setError(t("validation.memberRequired"));
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setError(null);

      const expenseData = {
        name: formData.name.trim(),
        value: parseFloat(formData.value),
        type: formData.type,
        user: formData.user,
        asset: formData.asset,
      };

      if (editingExpense) {
        await updateExpense(expenseData);
      } else {
        await createExpense(expenseData);
      }

      onExpenseCreated();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleClose = () => {
    setFormData({
      name: "",
      value: "",
      type: "static",
      user: "",
      asset: "",
    });
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {editingExpense ? t("modal.titleEdit") : t("modal.titleAdd")}
      </DialogTitle>
      <DialogContent>
        {dataLoading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ pt: 2 }}>
            {error && (
              <Alert severity="error" sx={{ mb: SPACING.sm }}>
                {error}
              </Alert>
            )}

            <TextField
              fullWidth
              label={t("modal.expenseName")}
              value={formData.name}
              onChange={(e) => handleInputChange("name", e.target.value)}
              placeholder={t("modal.expenseNamePlaceholder")}
              sx={{ mb: SPACING.sm }}
            />

            <Box sx={{ display: "flex", gap: 2, mb: SPACING.sm }}>
              <FormControl sx={{ minWidth: 140 }}>
                <InputLabel>{t("modal.type")}</InputLabel>
                <Select
                  value={formData.type}
                  label={t("modal.type")}
                  onChange={(e) => handleInputChange("type", e.target.value)}
                >
                  <MenuItem value="static">{t("modal.typeStatic")}</MenuItem>
                  <MenuItem value="percentage">
                    {t("modal.typePercentage")}
                  </MenuItem>
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label={t("modal.value")}
                type="number"
                value={formData.value}
                onChange={(e) => handleInputChange("value", e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      {formData.type === "percentage" ? "%" : "$"}
                    </InputAdornment>
                  ),
                }}
                inputProps={{
                  min: 0,
                  max: formData.type === "percentage" ? 100 : undefined,
                  step: "0.01",
                }}
              />
            </Box>

            <FormControl fullWidth sx={{ mb: SPACING.sm }}>
              <InputLabel>{t("modal.projectMember")}</InputLabel>
              <Select
                value={formData.user}
                label={t("modal.projectMember")}
                onChange={(e) => handleInputChange("user", e.target.value)}
              >
                {members.map((member) => (
                  <MenuItem key={member.id} value={member.id}>
                    {member.name} ({member.role})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth sx={{ mb: SPACING.sm }}>
              <InputLabel>{t("modal.asset")}</InputLabel>
              <Select
                value={formData.asset}
                label={t("modal.asset")}
                onChange={(e) => handleInputChange("asset", e.target.value)}
              >
                {assets.map((asset) => (
                  <MenuItem key={asset.id} value={asset.id}>
                    {asset.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} variant="secondary" disabled={loading}>
          {t("modal.cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || dataLoading}
        >
          {loading ? (
            <CircularProgress size={20} />
          ) : editingExpense ? (
            t("modal.update")
          ) : (
            t("modal.create")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddExpenseModal;
